import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve } from '@angular/router';

import { take } from 'rxjs';

import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { CurationAgentsService } from '../../services/curation-agents.service';
import { applyAgentScope } from '../../models/agent-scope.util';

/**
 * Scopes the mention feed to one agent. The feed itself is the shared monitoring one, which reads
 * its starting filters from `route.data.filters` - the same hook the topic and folder routes use -
 * so all this has to do is hand it a query carrying advanced monitoring's `aiAgent` filter.
 */
@Injectable()
export class AgentMentionsFilterResolver implements Resolve<MentionSearchCriteriaModel> {

    constructor(
        private curationAgentsService: CurationAgentsService
    ) {}

    resolve(route: ActivatedRouteSnapshot): MentionSearchCriteriaModel {
        const agentId = Number(route.parent?.paramMap.get('agent_id'));

        if (!agentId) {
            return null;
        }

        // A link in from elsewhere - a report drill down, a briefing's view source - carries the
        // filters it was showing, so the feed opens on those rather than on a blank search
        const linkedCriteria = route.queryParams['filters']
            ? MentionSearchCriteriaModel.fromFilterQueryString(route.queryParams['filters'])?.search
            : null;

        // Agents act on saved coverage, so this is an account search. Marked here as well as in
        // AgentMentionsComponent: setSearchCriteria picks the filter list off this flag, and an
        // unmarked criteria reaching it first leaves the feed on the shorter mercury list.
        const criteria = linkedCriteria ?? new MentionSearchCriteriaModel();
        criteria.isAccountSearch = true;

        // Whatever the sidebar has ticked, so the feed opens already narrowed rather than showing
        // everything for a moment. AgentMentionsComponent keeps these in step from here on.
        this.curationAgentsService.agentScope$.pipe(take(1)).subscribe(agentScope =>
            applyAgentScope(criteria.query, agentId, agentScope));

        // A linked window becomes the shared one, so AgentMentionsComponent does not swap it back
        if (linkedCriteria?.query?.dateQuery) {
            this.curationAgentsService.setSelectedDateRange(linkedCriteria.query.dateQuery);

            return criteria;
        }

        // A fresh criteria defaults to the last 24 hours, and the feed's base sets its date from
        // this query - which would otherwise stamp on whatever window the other sections are using
        this.curationAgentsService.selectedDateRange$.pipe(take(1)).subscribe(dateRange => {
            if (dateRange) {
                criteria.query.dateQuery = dateRange;
            }
        });

        return criteria;
    }
}
